import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MemberZipaBalancesV1, MemberTimedPointsBalancesV1 } from '@pokerroom/types';
import { api } from '../../lib/api';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';

const field =
  'min-h-11 w-full rounded-md border border-base-300 bg-base-100 p-2 focus:ring-2 focus:ring-primary';
const button =
  'inline-flex min-h-11 items-center rounded-md border border-base-300 px-3 py-2 text-sm disabled:opacity-50';
const positiveInt = (value: string | null, fallback: number, max: number) =>
  value && /^\d+$/.test(value) && Number(value) >= 1 && Number(value) <= max
    ? Number(value)
    : fallback;
/** Group exact decimal strings without converting money to floating point. */
export const formatZipaBalance = (value: string) => value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export function MemberTimedPointsBalanceList() {
  return <MemberZipaBalanceList variant="timed" />;
}

export function MemberZipaBalanceList({ variant = 'zipa' }: { variant?: 'zipa' | 'timed' }) {
  const identity = useAuthStore((s) =>
    JSON.stringify([
      s.user?.id,
      s.sessionRevision,
      sessionVenueKey(s.accessToken),
      s.allowedActions,
    ]),
  );
  return <BalanceContent key={`${variant}:${identity}`} identity={identity} variant={variant} />;
}

function BalanceContent({ identity, variant }: { identity: string; variant: 'zipa' | 'timed' }) {
  const timed = variant === 'timed';
  const labels = timed ? 'timedBalances' : 'balances';
  const { t, i18n } = useTranslation('member');
  const [params, setParams] = useSearchParams();
  const venueId = useAuthStore((s) => sessionVenueKey(s.accessToken));
  const canRead = useAuthStore((s) => s.allowedActions?.includes('member:read') === true);
  const search = params.get('search') ?? '';
  const sign = ['positive', 'negative'].includes(params.get('sign') ?? '')
    ? params.get('sign')!
    : 'all';
  const sort = params.get('sort') === 'balance_asc' ? 'balance_asc' : 'balance_desc';
  const page = positiveInt(params.get('page'), 1, 2147483647);
  const limit = positiveInt(params.get('limit'), 20, 100);
  const [draft, setDraft] = useState(search);
  useEffect(() => {
    setDraft((current) => (current.trim() === search ? current : search));
  }, [search]);
  useEffect(() => {
    if (draft.trim() === search) return;
    const timer = setTimeout(() => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('search', draft.trim());
          next.set('page', '1');
          return next;
        },
        { replace: true },
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [draft, search, setParams]);
  const update = (key: string, value: string) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.set(key, value);
      if (key !== 'page') next.set('page', '1');
      return next;
    });
  };
  const result = useQuery({
    queryKey: [
      timed ? 'member-timed-points-balances' : 'member-zipa-balances',
      venueId,
      identity,
      sign,
      search,
      page,
      limit,
      sort,
    ],
    enabled: canRead && !!venueId,
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
    queryFn: ({ signal }) =>
      api.get<MemberZipaBalancesV1 | MemberTimedPointsBalancesV1>(
        timed ? '/members/timed-points/balances' : '/members/zipa/balances',
        {
          params: { sign, search, page, limit, sort },
          signal,
        },
      ),
  });
  // An errored/foreign response must never appear as a successful zero or stale balance.
  const data = !result.isError && result.data?.venueId === venueId ? result.data : undefined;
  const pages = data ? Math.max(1, Math.ceil(data.filteredCount / limit)) : 1;
  useEffect(() => {
    if (data && page > pages)
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('page', String(pages));
          return next;
        },
        { replace: true },
      );
  }, [data, page, pages, setParams]);
  if (!canRead || !venueId) return <p role="alert">{t('balances.denied')}</p>;
  return (
    <section
      className="space-y-4 min-w-0"
      aria-label={t(`${labels}.title`)}
      aria-busy={result.isFetching}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t(`${labels}.title`)}</h2>
          {data && (
            <p className="text-sm text-base-content/70 break-words">
              {data.venueName} ·{' '}
              {timed && 'period' in data && (
                <>{t('timedBalances.period', { period: data.period })} · </>
              )}
              {t('balances.asOf', {
                time: new Date(data.asOf).toLocaleString(i18n.language),
              })}
            </p>
          )}
        </div>
        <button
          type="button"
          className={button}
          disabled={result.isFetching}
          onClick={() => void result.refetch()}
        >
          {t('balances.reload')}
        </button>
      </div>
      <p className="text-sm text-base-content/70">{t(`${labels}.help`)}</p>
      {data && (
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {(['positiveTotal', 'negativeTotal', 'netTotal', 'nonzeroCount'] as const).map((key) => (
            <div key={key} className="rounded-lg border border-base-300 bg-base-200 p-3">
              <dt className="text-sm text-base-content/70">{t(`balances.${key}`)}</dt>
              <dd
                className={`text-xl font-semibold tabular-nums break-words ${key === 'negativeTotal' ? 'text-error' : ''}`}
              >
                {formatZipaBalance(String(data.venueSummary[key]))}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label>
          {t('list.searchLabel')}
          <input
            className={field}
            type="search"
            maxLength={100}
            value={draft}
            placeholder={t('balances.search')}
            onChange={(e) => setDraft(e.target.value)}
          />
        </label>
        <label>
          {t('balances.sign')}
          <select
            aria-label={t('balances.sign')}
            className={field}
            value={sign}
            onChange={(e) => update('sign', e.target.value)}
          >
            {['all', 'positive', 'negative'].map((key) => (
              <option key={key} value={key}>
                {t(`balances.${key}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('balances.sort')}
          <select
            aria-label={t('balances.sort')}
            className={field}
            value={sort}
            onChange={(e) => update('sort', e.target.value)}
          >
            {['balance_desc', 'balance_asc'].map((key) => (
              <option key={key} value={key}>
                {t(`balances.${key}`)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t('balances.limit')}
          <select
            aria-label={t('balances.limit')}
            className={field}
            value={limit}
            onChange={(e) => update('limit', e.target.value)}
          >
            {[...new Set([20, 50, 100, limit])]
              .sort((a, b) => a - b)
              .map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
          </select>
        </label>
      </div>
      {result.isError ? (
        <div role="alert">
          <p>{t('list.loadError')}</p>
          <button className={button} type="button" onClick={() => void result.refetch()}>
            {t('list.retry')}
          </button>
        </div>
      ) : !data ? (
        <p role="status">{t('balances.loading')}</p>
      ) : (
        page <= pages && (
          <>
            <p aria-live="polite">
              {t('balances.filtered', {
                count: data.filteredCount,
                amount: formatZipaBalance(data.filteredNet),
              })}
            </p>
            {data.rows.length === 0 ? (
              <p>{t(`${labels}.empty`)}</p>
            ) : (
              <>
                <ul className="space-y-3 md:hidden">
                  {data.rows.map((row) => (
                    <li
                      key={row.memberId}
                      className="rounded-lg border border-base-300 bg-base-200 p-4 space-y-2"
                    >
                      <Link
                        className="inline-flex min-h-11 items-center text-primary underline font-semibold [overflow-wrap:anywhere]"
                        to={`/members/${row.memberId}?tab=${timed && 'period' in data ? encodeURIComponent(`timed:${data.period}`) : 'zipa'}`}
                      >
                        {row.nickname}
                      </Link>
                      <p className="font-mono text-sm [overflow-wrap:anywhere]">
                        <Link
                          className="inline-flex min-h-11 items-center text-primary underline"
                          to={`/members/${row.memberId}?tab=${timed && 'period' in data ? encodeURIComponent(`timed:${data.period}`) : 'zipa'}`}
                        >
                          {data.associationMode && row.associationMemberNo
                            ? row.associationMemberNo
                            : row.memberNo}
                        </Link>
                        {data.associationMode && !row.associationMemberNo && (
                          <span className="block text-xs">{t('balances.localNumber')}</span>
                        )}
                      </p>
                      <dl className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <dt className="text-base-content/70">{t('balances.localStatus')}</dt>
                          <dd>{t(`status_${row.localStatus}`)}</dd>
                        </div>
                        {(!timed || data.associationMode) && (
                          <div>
                            <dt className="text-base-content/70">
                              {t('balances.associationStatus')}
                            </dt>
                            <dd>
                              {row.associationStatus ? t(`status_${row.associationStatus}`) : '—'}
                            </dd>
                          </div>
                        )}
                        <div className="col-span-2 flex justify-between gap-3 border-t border-base-300 pt-2">
                          <dt>{t(`${labels}.balance`)}</dt>
                          <dd
                            className={`font-mono tabular-nums font-semibold ${row.balance.startsWith('-') ? 'text-error' : ''}`}
                          >
                            {formatZipaBalance(row.balance)}
                          </dd>
                        </div>
                      </dl>
                    </li>
                  ))}
                </ul>
                <div className="hidden md:block overflow-x-auto rounded-lg border border-base-300">
                  <table className="w-full text-sm [&_th]:p-3 [&_td]:p-3">
                    <caption className="sr-only">{t(`${labels}.title`)}</caption>
                    <thead className="bg-base-200">
                      <tr>
                        {[
                          'memberNo',
                          'nickname',
                          'balances.localStatus',
                          ...(!timed || data.associationMode ? ['balances.associationStatus'] : []),
                          `${labels}.balance`,
                        ].map((key) => (
                          <th
                            key={key}
                            scope="col"
                            className={key === `${labels}.balance` ? 'text-right' : 'text-left'}
                          >
                            {t(key)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-base-300">
                      {data.rows.map((row) => (
                        <tr key={row.memberId}>
                          <td className="font-mono">
                            <Link
                              className="inline-flex min-h-11 items-center text-primary underline"
                              to={`/members/${row.memberId}?tab=${timed && 'period' in data ? encodeURIComponent(`timed:${data.period}`) : 'zipa'}`}
                            >
                              {data.associationMode && row.associationMemberNo
                                ? row.associationMemberNo
                                : row.memberNo}
                            </Link>
                            {data.associationMode && !row.associationMemberNo && (
                              <span className="block text-xs">{t('balances.localNumber')}</span>
                            )}
                          </td>
                          <td className="break-words">
                            <Link
                              className="inline-flex min-h-11 items-center text-primary underline"
                              to={`/members/${row.memberId}?tab=${timed && 'period' in data ? encodeURIComponent(`timed:${data.period}`) : 'zipa'}`}
                            >
                              {row.nickname}
                            </Link>
                          </td>
                          <td>{t(`status_${row.localStatus}`)}</td>
                          {(!timed || data.associationMode) && (
                            <td>
                              {row.associationStatus ? t(`status_${row.associationStatus}`) : '—'}
                            </td>
                          )}
                          <td
                            className={`text-right font-mono tabular-nums ${row.balance.startsWith('-') ? 'text-error' : ''}`}
                          >
                            {formatZipaBalance(row.balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            <nav
              aria-label={t('list.pagination')}
              className="flex flex-wrap items-center justify-end gap-3"
            >
              <button
                type="button"
                className={button}
                disabled={page <= 1 || result.isFetching}
                onClick={() => update('page', String(page - 1))}
              >
                {t('prevPage')}
              </button>
              <span>
                {page} / {pages}
              </span>
              <button
                type="button"
                className={button}
                disabled={page >= pages || result.isFetching}
                onClick={() => update('page', String(page + 1))}
              >
                {t('nextPage')}
              </button>
            </nav>
          </>
        )
      )}
    </section>
  );
}
